/* eslint-disable max-lines-per-function */
import React, { ChangeEvent } from 'react'
import { State } from '@hookstate/core'
import { Button, Collapse, Grid, TextField } from '@mui/material'
import { useLang } from '~/hooks/useLang'
import FilterAutocompleteType from '~/components/FilterAutocompleteType'
import FilterAutocompleteSubtypesByType from '~/components/FilterAutocompleteSubtypeByType'
import FilterSelectPeriod from '~/components/FilterAutocompletePeriod'
import FilterAutocompleteAgency from '~/components/FilterAutocompleteAgency'
import { Dayjs } from 'dayjs'
import { LoadingButton } from '@mui/lab'
import FilterAutocompleteCity from '~/components/FilterSelectAutocompleteCity'
import FilterAutocompleteFetch from '~/components/FilterAutocompleteFetch'
import FilterAutocompleteEntryStatus from '~/components/FilterAutocompleteEntryState'
import FilterTextField from '~/components/FilterTextField'
import InputOccurrenceNumber from '~/components/InputOccurrenceNumber'
import FilterAutocompleteDistrict from '~/components/FilterSelectAutocompleteDistrict'
import PhoneTextField from '~/components/PhoneField'
import FilterSwitch from '~/components/FilterSwitch'
import EntryOriginSelect from '../EntryOriginSelect'

interface EntryFiltersDashboardProps {
  readonly loading: State<boolean>
  readonly agencyId: State<number | null>
  readonly initialPeriod: State<string>
  readonly typeId: State<number | null>
  readonly subtypeId?: State<number | null>
  readonly userId: State<number | null>
  readonly entryStatusId: State<number | null>
  readonly location: State<string | null>
  readonly occurrenceId: State<string | null>
  readonly districtId: State<number | null>
  readonly hidden?: boolean
  readonly searchButton: () => void
  readonly clearFilters: () => void
  readonly onStartDateChange: (value: Dayjs | null) => void
  readonly onFinishDateChange: (value: Dayjs | null) => void
  readonly onPeriodChange: (value: string) => void
  readonly cityId?: State<number | null>
  readonly focusChangeUserId?: State<boolean>
  readonly resetValueUserId?: State<boolean>
  readonly onlyOccurrence: State<boolean>
  readonly entryOriginId?: State<number | null>
  readonly entryOriginStation: State<number | null>
  readonly requester: State<string | null>
  readonly phone: State<string | null>
  readonly disabledSendButton: () => boolean
}

export default function EntryFiltersDashboard({
  onPeriodChange,
  initialPeriod,
  searchButton,
  clearFilters,
  agencyId,
  hidden,
  typeId,
  subtypeId,
  onStartDateChange,
  onFinishDateChange,
  loading,
  cityId,
  userId,
  focusChangeUserId,
  resetValueUserId,
  entryStatusId,
  location,
  occurrenceId,
  districtId,
  phone,
  onlyOccurrence,
  entryOriginId,
  entryOriginStation,
  requester,
  disabledSendButton,
}: EntryFiltersDashboardProps) {
  const { translate } = useLang()
  return (
    <Collapse in={hidden}>
      <Grid
        container
        xs={12}
        sx={{
          width: '100%',
          display: 'flex',
          alignItems: 'center',
          p: 4,
        }}
      >
        <Grid container direction={'row'}>
          <Grid item xs={2} sx={{ pl: 1, pt: 1 }}>
            <InputOccurrenceNumber
              occurrence={occurrenceId}
              onOccurrenceChange={(event: ChangeEvent<HTMLInputElement>) => {
                occurrenceId.set(event.target.value)
              }}
            />
          </Grid>
          <Grid item xs={2} sx={{ pl: 1, pt: 1 }}>
            <FilterAutocompleteAgency
              agencyId={agencyId.get()}
              onAgencyChange={(agency) => agencyId.set(agency)}
            />
          </Grid>
          <Grid item xs={2} sx={{ pl: 1, pt: 1 }}>
            <FilterAutocompleteType
              typeId={typeId.get()}
              onTypeChange={(type) => typeId.set(Number(type?.id) ?? null)}
            />
          </Grid>
          <Grid item xs={2} sx={{ pl: 1, pt: 1 }}>
            <FilterAutocompleteSubtypesByType
              hasType
              typeId={typeId.get()}
              onSubtypeChange={(subtype) => subtypeId?.set(subtype?.id ?? null)}
            />
          </Grid>
          <Grid item xs={2} sx={{ pl: 1, pt: 1 }}>
            <FilterAutocompleteCity
              cityId={cityId?.get()}
              onCityChange={(city) => cityId?.set(city)}
            />
          </Grid>
          <Grid item xs={2} sx={{ pl: 1, pt: 1 }}>
            <FilterAutocompleteDistrict
              disabled={!cityId?.get()}
              onDistrictChange={(district) => {
                districtId?.set(district)
              }}
              districtId={districtId}
              cityId={cityId ? cityId.get() : null}
            />
          </Grid>
          <Grid item xs={2} sx={{ pl: 1, pt: 1 }}>
            <FilterTextField
              currentText={location.get()}
              label={'Location'}
              onTextFieldChange={(textField) => {
                location.set(textField)
              }}
            />
          </Grid>
          <Grid item xs={2} sx={{ pl: 1, pt: 1 }}>
            <EntryOriginSelect
              isValid={true}
              isInvalidRequest={false}
              originId={entryOriginId}
              originStation={entryOriginStation.get()}
              hasValueDefault={false}
            />
          </Grid>

          <Grid item xs={2} sx={{ pl: 1, pt: 1 }}>
            <PhoneTextField
              statePhone={phone}
              disabledLastCalls
              disabled={false}
              phoneRequesterPlaceholderLabel
            />
          </Grid>

          <Grid item xs={2} sx={{ pl: 1, pt: 1 }}>
            <TextField
              fullWidth
              label={translate('Requester')}
              size="small"
              value={requester?.get() ?? ''}
              onChange={(event: ChangeEvent<HTMLInputElement>) => {
                requester?.set(event.target.value)
              }}
            />
          </Grid>

          <Grid item xs={2} sx={{ pl: 1, pt: 1 }}>
            <FilterAutocompleteFetch
              label={'User'}
              urlFetch={'/user/get'}
              size={'small'}
              disabled={false}
              onFetchChange={(user: number | null) => userId?.set(user)}
              resetValues={resetValueUserId?.get()}
              focusChange={focusChangeUserId}
              // model='user'
              valueId={userId?.get() ? Number(userId?.get()) : undefined}
            />
          </Grid>
          <Grid item xs={2} sx={{ pl: 1, pt: 1 }}>
            <FilterAutocompleteEntryStatus
              entryStatus={entryStatusId.get()}
              onEntryStatusChange={(entryStatus) => {
                entryStatusId?.set(entryStatus)
              }}
            />
          </Grid>
          <FilterSelectPeriod
            onStartDateChange={onStartDateChange}
            onFinishDateChange={onFinishDateChange}
            onPeriodChange={onPeriodChange}
            period={initialPeriod.get()}
            requiredStartDate
          />
          <Grid item xs={2.5} sx={{ pl: 2, pt: 1 }}>
            <FilterSwitch
              label={translate('Only services that generated incidents')}
              onCheckedChange={() => {
                onlyOccurrence?.set(!onlyOccurrence.value)
              }}
              checked={onlyOccurrence?.get()}
            />
          </Grid>
        </Grid>
        <Grid
          container
          direction={'row'}
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'end',
            pt: 2,
            gap: 2,
          }}
        >
          <Button variant="outlined" onClick={clearFilters}>
            {translate('Clear Filters')}
          </Button>
          <LoadingButton
            loading={loading.get()}
            variant="contained"
            onClick={searchButton}
            disabled={disabledSendButton()}
          >
            {translate('Search')}
          </LoadingButton>
        </Grid>
      </Grid>
    </Collapse>
  )
}
